import { Fragment, ReactNode, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconBrandNationalRegistry } from '@components/icons/brand-national-registry';
import { IconRadioChecked } from '@components/icons/radio-checked';
import { IconRadioIdle } from '@components/icons/radio-idle';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';

import { useStore } from 'models/Provider';

import { FilterActionButtons } from './filter-action-buttons';

const FIELDS = [
  { key: 'name' },
  { key: 'gender' },
  { key: 'ssn' },
  { key: 'age' },
  { key: 'age_year_end' },
  { key: 'street' },
  { key: 'city' },
  { key: 'postal_code' },
] as Array<{ key: string }>;

const COMPARERS: Record<(typeof FIELDS)[number]['key'], Array<FilterComparer>> = {
  name: ['is', 'not_is', 'starts'],
  gender: ['is', 'not_is', 'any_of', 'none_of'],
  ssn: ['is', 'not_is', 'contains', 'not_contains'],
  street: ['is', 'not_is', 'contains', 'not_contains'],
  city: ['is', 'not_is', 'contains', 'not_contains'],
  age: ['is', 'not_is', 'gt', 'lt'],
  age_year_end: ['is', 'not_is', 'gt', 'lt'],
  postal_code: ['is', 'not_is'],
};

function FilterApplicationNationalRegistryApplied({ data }: FilterAppliedProps) {
  const { t } = useStore();

  let valueLabel = '';
  if (data.field === 'gender') {
    const values = Array.isArray(data.value) ? data.value : [data.value];
    valueLabel = values.map((value) => t(`ssnregistry.gender.${value}`)).join(', ');
  } else {
    valueLabel = Array.isArray(data.value) ? data.value.join(', ') : (data.value as string);
  }

  return (
    <Filters.AppliedFilter
      icon={<IconBrandNationalRegistry size={24} />}
      category={t('applicants.filters.national_registry.' + data.field)}
      value={valueLabel}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationNationalRegistryMenu() {
  const { t } = useStore();
  const { state, actions, hasValue, isMultiComparer } = useCurrentFilter();

  const isAgeField = state.field === 'age' || state.field === 'age_year_end';

  const genderItems = ['1', '2', '7'].map((genderId) => ({
    value: genderId,
    label: t(`ssnregistry.gender.${genderId}`),
  }));

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.national_registry.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      {state.field && (
        <Filters.ListItem
          heading
          label={t('applicants.filters.national_registry.' + state.field)}
          icon={<IconArrowLeft />}
          onClick={actions.unselectField}
        />
      )}

      {state.field === 'gender' && state.comparer && (
        <Filters.ListItem
          heading
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
          label={t('comparers.' + state.comparer)}
        />
      )}

      <Filters.ListSeparator />

      {!state.field && (
        <Fragment>
          {FIELDS.map((field) => (
            <Filters.ListItem
              key={field.key}
              label={t('applicants.filters.national_registry.' + field.key)}
              onClick={() => actions.selectField(field.key)}
            />
          ))}
        </Fragment>
      )}

      {state.field && state.field !== 'gender' && (
        <Fragment>
          {COMPARERS[state.field].map((comparer) => {
            const isCurrentComparer = state.comparer === comparer;
            const label = t('comparers.' + comparer).replace('...', '');
            let extra: ReactNode = null;
            if (isCurrentComparer) {
              extra = (
                <TextField
                  autoFocus
                  onChange={actions.selectValue}
                  defaultValue={state.value as string}
                  label={label}
                  hideLabel
                />
              );
              if (isAgeField) {
                extra = (
                  <Stack orientation="horizontal" space={2} align="center">
                    {extra}
                    <Text font="regular14" color="purple80" text={t('filters.years')} />
                  </Stack>
                );
              }
            }

            return (
              <Fragment key={comparer}>
                <Filters.ListItem
                  icon={isCurrentComparer ? <IconRadioChecked /> : <IconRadioIdle />}
                  label={label}
                  onClick={() => actions.selectComparer(comparer)}
                />
                {extra}
              </Fragment>
            );
          })}
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}

      {state.field === 'gender' && !state.comparer && (
        <Fragment>
          {COMPARERS[state.field].map((comparer) => (
            <Filters.ListItem
              key={comparer}
              label={t('comparers.' + comparer)}
              onClick={() => actions.selectComparer(comparer)}
            />
          ))}
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}

      {state.field === 'gender' && state.comparer && (
        <Fragment>
          <Filters.FilterValueMenu
            items={genderItems}
            isMulti={isMultiComparer}
            onSetValue={actions.selectValue}
            state={state}
          />
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

export function useFilterApplicationNationalRegistry(): FilterConfig | null {
  const { t, current } = useStore();

  return useMemo(() => {
    if (!current.info.useSsnRegistry) return null;

    return {
      category: 'national-registr',
      label: t('applicants.filters.national_registry.title'),
      icon: <IconBrandNationalRegistry size={24} />,
      AppliedFilter: FilterApplicationNationalRegistryApplied,
      Menu: FilterApplicationNationalRegistryMenu,
      toQueryData: (filter) => {
        let field = filter.field as string;

        if (field === 'gender') {
          let value = Array.isArray(filter.value) ? [...filter.value] : [filter.value];

          // Each gender has two corresponding values (underage and adult).
          // We only display one for simplicity, but we need to filter for both.
          // 1 and 3 are male, 2 and 4 are female, 7 and 8 are non-binary/other.
          value.includes('1') && value.push('3');
          value.includes('2') && value.push('4');
          value.includes('7') && value.push('8');

          switch (filter.comparer) {
            case 'is':
            case 'any_of':
              return { [`ssn_registry__${field}__any_of`]: value };
            case 'not_is':
            case 'none_of':
              return { [`ssn_registry__${field}__none_of`]: value };
            default:
              return {};
          }
        } else {
          let value = filter.value as string;

          switch (filter.comparer) {
            case 'is':
              return { [`ssn_registry__${field}`]: value.toLowerCase() };
            case 'starts':
              return { [`ssn_registry__${field}__startswith`]: value.toLowerCase() };
            case 'not_is':
              return { [`ssn_registry__${field}__is_not`]: value.toLowerCase() };
            case 'contains':
              return { [`ssn_registry__${field}__contains`]: value.toLowerCase() };
            case 'not_contains':
              return { [`ssn_registry__${field}__not_contains`]: value.toLowerCase() };
            case 'gt':
              return { [`ssn_registry__${field}__gt`]: value };
            case 'lt':
              return { [`ssn_registry__${field}__lt`]: value };
            default:
              return {};
          }
        }
      },
    };
  }, [current, t]);
}
